import React, { useContext } from 'react';
import { ArrowLeft, ListChecks } from 'lucide-react';
import '../../../assets/styles/ChooseSurvey.css';

import PriceCard from '../../common/PriceCard';
import SectionEyebrow from '../../common/SectionEyebrow';
import { TemplateContext } from '../../../assets/js/TemplateContext';
import { useLanguage } from '../../../assets/js/LanguageContext';

const SurveyTemplateSelector = ({ onBack }) => {
  const { updateTemplate } = useContext(TemplateContext);
  const { t } = useLanguage();

  const sharedItems = [
    t('template_item_satisfaction'),
    t('template_item_internal_communication'),
    t('template_item_relationships'),
    t('template_item_work_conditions'),
    t('template_item_growth'),
    t('template_item_balance'),
    t('template_item_recognition')
  ];

  const templates = [
    {
      title: t('template_climate_title'),
      items: sharedItems,
      questions: [
        { pregunta: t('template_climate_q1'), tipo: 'rating', configurada: true },
        { pregunta: t('template_climate_q2'), tipo: 'open', configurada: true },
        { pregunta: t('template_climate_q3'), tipo: 'open', configurada: true }
      ]
    },
    {
      title: t('template_profile_title'),
      items: [
        t('template_profile_region'),
        t('template_profile_gender'),
        t('template_profile_age'),
        t('template_profile_education'),
        t('template_profile_contract'),
        t('template_profile_staff'),
        t('template_profile_recommend'),
        t('template_profile_reason'),
        t('template_profile_comments')
      ],
      questions: [
        { id: '1', pregunta: t('template_profile_region'), tipo: 'open', configurada: true },
        { id: '2', pregunta: t('template_profile_gender'), tipo: 'open', configurada: true },
        { id: '3', pregunta: t('template_profile_age'), tipo: 'open', configurada: true },
        { id: '4', pregunta: t('template_profile_education_q'), tipo: 'open', configurada: true },
        { id: '5', pregunta: t('template_profile_contract_q'), tipo: 'open', configurada: true },
        { id: '6', pregunta: t('template_profile_staff_q'), tipo: 'closed', opciones: [t('common_yes'), t('common_no')], configurada: true },
        { id: '7', pregunta: t('template_profile_recommend_q'), tipo: 'rating', minValue: 0, maxValue: 10, configurada: true },
        { id: '8', pregunta: t('template_profile_reason_q'), tipo: 'open', configurada: true },
        { id: '9', pregunta: t('template_profile_comments_q'), tipo: 'open', configurada: true }
      ]
    },
    {
      title: t('template_team_close_title'),
      items: sharedItems,
      questions: [
        { pregunta: t('template_team_close_q1'), tipo: 'rating', configurada: true },
        { pregunta: t('template_team_words_q'), tipo: 'open', configurada: true },
        { pregunta: t('template_team_reason_q'), tipo: 'open', configurada: true },
        { pregunta: t('template_team_close_q4'), tipo: 'open', configurada: true }
      ]
    },
    {
      title: t('template_team_org_title'),
      items: sharedItems,
      questions: [
        { pregunta: t('template_team_org_q1'), tipo: 'rating', configurada: true },
        { pregunta: t('template_team_words_q'), tipo: 'open', configurada: true },
        { pregunta: t('template_team_reason_q'), tipo: 'open', configurada: true },
        { pregunta: t('template_team_org_q4'), tipo: 'open', configurada: true }
      ]
    },
    {
      title: t('template_leadership_title'),
      items: [
        t('template_leadership_item1'),
        t('template_leadership_item2'),
        t('template_leadership_item3'),
        t('template_leadership_item4'),
        t('template_leadership_item5')
      ],
      questions: [
        { pregunta: t('template_leadership_q1'), tipo: 'rating', minValue: 1, maxValue: 5, configurada: true },
        { pregunta: t('template_leadership_q2'), tipo: 'rating', minValue: 1, maxValue: 5, configurada: true },
        { pregunta: t('template_leadership_q3'), tipo: 'closed', opciones: [
          t('template_leadership_q3_opt1'),
          t('template_leadership_q3_opt2'),
          t('template_leadership_q3_opt3'),
          t('template_leadership_q3_opt4'),
          t('template_leadership_q3_opt5')
        ], configurada: true },
        { pregunta: t('template_leadership_q4'), tipo: 'open', configurada: true },
        { pregunta: t('template_leadership_q5'), tipo: 'open', configurada: true }
      ]
    },
    {
      title: t('template_wellbeing_title'),
      items: [
        t('template_wellbeing_item1'),
        t('template_wellbeing_item2'),
        t('template_wellbeing_item3'),
        t('template_wellbeing_item4'),
        t('template_wellbeing_item5')
      ],
      questions: [
        { pregunta: t('template_wellbeing_q1'), tipo: 'rating', minValue: 1, maxValue: 5, configurada: true },
        { pregunta: t('template_wellbeing_q2'), tipo: 'rating', minValue: 1, maxValue: 10, configurada: true },
        { pregunta: t('template_wellbeing_q3'), tipo: 'closed', opciones: [
          t('template_wellbeing_q3_opt1'),
          t('template_wellbeing_q3_opt2'),
          t('template_wellbeing_q3_opt3'),
          t('template_wellbeing_q3_opt4')
        ], configurada: true },
        { pregunta: t('template_wellbeing_q4'), tipo: 'open', configurada: true },
        { pregunta: t('template_wellbeing_q5'), tipo: 'open', configurada: true }
      ]
    },
    {
      title: t('template_onboarding_title'),
      items: [
        t('template_onboarding_item1'),
        t('template_onboarding_item2'),
        t('template_onboarding_item3'),
        t('template_onboarding_item4'),
        t('template_onboarding_item5')
      ],
      questions: [
        { pregunta: t('template_onboarding_q1'), tipo: 'rating', minValue: 1, maxValue: 10, configurada: true },
        { pregunta: t('template_onboarding_q2'), tipo: 'closed', opciones: [
          t('template_onboarding_q2_opt1'),
          t('template_onboarding_q2_opt2'),
          t('template_onboarding_q2_opt3'),
          t('template_onboarding_q2_opt4')
        ], configurada: true },
        { pregunta: t('template_onboarding_q3'), tipo: 'closed', opciones: [
          t('template_onboarding_q3_opt1'),
          t('template_onboarding_q3_opt2'),
          t('template_onboarding_q3_opt3'),
          t('template_onboarding_q3_opt4')
        ], configurada: true },
        { pregunta: t('template_onboarding_q4'), tipo: 'open', configurada: true },
        { pregunta: t('template_onboarding_q5'), tipo: 'open', configurada: true }
      ]
    },
    {
      title: t('template_diversity_title'),
      items: [
        t('template_diversity_item1'),
        t('template_diversity_item2'),
        t('template_diversity_item3'),
        t('template_diversity_item4'),
        t('template_diversity_item5')
      ],
      questions: [
        { pregunta: t('template_diversity_q1'), tipo: 'rating', minValue: 1, maxValue: 5, configurada: true },
        { pregunta: t('template_diversity_q2'), tipo: 'rating', minValue: 1, maxValue: 5, configurada: true },
        { pregunta: t('template_diversity_q3'), tipo: 'closed', opciones: [
          t('template_diversity_q3_opt1'),
          t('template_diversity_q3_opt2'),
          t('template_diversity_q3_opt3'),
          t('template_diversity_q3_opt4')
        ], configurada: true },
        { pregunta: t('template_diversity_q4'), tipo: 'open', configurada: true },
        { pregunta: t('template_diversity_q5'), tipo: 'open', configurada: true }
      ]
    },
    {
      title: t('template_hybrid_title'),
      items: [
        t('template_hybrid_item1'),
        t('template_hybrid_item2'),
        t('template_hybrid_item3'),
        t('template_hybrid_item4'),
        t('template_hybrid_item5')
      ],
      questions: [
        { pregunta: t('template_hybrid_q1'), tipo: 'rating', minValue: 1, maxValue: 10, configurada: true },
        { pregunta: t('template_hybrid_q2'), tipo: 'closed', opciones: [
          t('template_hybrid_q2_opt1'),
          t('template_hybrid_q2_opt2'),
          t('template_hybrid_q2_opt3'),
          t('template_hybrid_q2_opt4'),
          t('template_hybrid_q2_opt5')
        ], configurada: true },
        { pregunta: t('template_hybrid_q3'), tipo: 'closed', opciones: ['0', '1', '2', '3', '4', '5'], configurada: true },
        { pregunta: t('template_hybrid_q4'), tipo: 'open', configurada: true },
        { pregunta: t('template_hybrid_q5'), tipo: 'open', configurada: true }
      ]
    }
  ];

  const handleSelectTemplate = (template) => {
    // Cada pregunta necesita un id propio: sin el, editar o borrar una afectaba a todas
    updateTemplate(template.title, template.questions.map((question, index) => ({ ...question, id: String(index + 1) })));
  };

  return (
    <div className="tp-choose tp-templates">
      <button type="button" className="tp-choose__back" onClick={onBack}>
        <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
        {t('common_back')}
      </button>

      <header className="tp-choose__head">
        <div className="tp-choose__heading">
          <SectionEyebrow>{t('choose_template')}</SectionEyebrow>
          <h1 className="tp-choose__title">{t('template_choose_title')}</h1>
          <p className="tp-choose__lead">{t('templates_lead')}</p>
        </div>
      </header>

      <div className="tp-price-grid tp-templates__grid">
        {templates.map((template, index) => (
          <PriceCard
            key={index}
            icon={<ListChecks size={22} strokeWidth={1.9} />}
            title={template.title}
            meta={`${template.questions.length} ${t('template_configured_questions')}`}
            features={template.items}
            ctaText={t('common_select')}
            onCta={() => handleSelectTemplate(template)}
          />
        ))}
      </div>
    </div>
  );
};

export default SurveyTemplateSelector;
